import { Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import type { CachedTopic } from "@/types/intent";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import {
  EXPORT_DATA_TYPES,
  EXPORT_INCLUSION_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  type ExportFieldConfiguration,
} from "./types";
import type { RadioOption } from "../RadioOptionsInput";
import { getCommonExportRatioOptions } from "./utils";
import { useIntentTopicRenderer } from "./hooks/useIntentTopicRenderer";
import { useIntentCollectionRenderer } from "./hooks/useIntentCollectionRenderer";
import {
  IntentThresholdSelector,
  type IntentSignalScoreUi,
} from "./components/IntentThresholdSelector";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  intentExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";

interface Props {
  isIntentFilterEmpty: boolean;
  intentInclusion: EXPORT_INCLUSION_TYPES;
  setIntentInclusion: (inclusion: EXPORT_INCLUSION_TYPES) => void;
  intentTopicIds: string[];
  setIntentTopicIds: (ids: string[]) => void;
  intentTopicsCache: CachedTopic[];
  setIntentTopicsCache: (cache: CachedTopic[]) => void;
  intentSignalScore: IntentSignalScoreUi;
  setIntentSignalScore: (value: IntentSignalScoreUi) => void;
  universeEnabled?: boolean;
  intentCollectionIds?: string[];
  setIntentCollectionIds?: (ids: string[]) => void;
  intentFieldConfiguration?: ExportFieldConfiguration;
  setIntentFieldConfiguration?: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
}

export const IntentExportOptions: React.FC<Props> = ({
  isIntentFilterEmpty,
  intentInclusion,
  setIntentInclusion,
  intentTopicIds,
  setIntentTopicIds,
  intentTopicsCache,
  setIntentTopicsCache,
  intentSignalScore,
  setIntentSignalScore,
  universeEnabled,
  intentCollectionIds,
  setIntentCollectionIds,
  intentFieldConfiguration,
  setIntentFieldConfiguration,
  enforceMandatoryFields,
}) => {
  const handleTopicSelectionChange = useCallback(
    (ids: string[], cache: CachedTopic[]) => {
      setIntentTopicIds(ids);
      setIntentTopicsCache(cache);
    },
    [setIntentTopicIds, setIntentTopicsCache]
  );

  const option1: RadioOption = {
    label:
      "Only include Intent Activity that matches the Intent filters selected",
    value: EXPORT_INCLUSION_TYPES.FILTERS,
    disabled: isIntentFilterEmpty,
    tooltipAsBubble: true,
    tooltip: isIntentFilterEmpty
      ? "Please add Intent Topics to your search in order to see specific Intent Activity in your exports"
      : undefined,
  };

  const option2: RadioOption = {
    label: "Include only selected topics",
    value: EXPORT_INCLUSION_TYPES.SELECTED,
  };

  const option5: RadioOption = {
    label: "Include only selected Intent Collections",
    value: EXPORT_INCLUSION_TYPES.SELECTED_COLLECTIONS,
  };

  const option3And4 = getCommonExportRatioOptions(EXPORT_DATA_TYPES.INTENT);
  // Matches OppGen's order: Filters, All, None, Selected topics, Selected collections.
  const options: RadioOption[] = [
    option1,
    ...option3And4,
    option2,
    ...(universeEnabled ? [option5] : []),
  ];

  const renderTopicContent = useIntentTopicRenderer(
    intentInclusion,
    intentTopicIds,
    intentTopicsCache,
    handleTopicSelectionChange
  );

  const renderCollectionContent = useIntentCollectionRenderer(
    intentInclusion,
    intentCollectionIds ?? [],
    setIntentCollectionIds ?? (() => {})
  );

  const renderOptionContent = useCallback(
    (optionValue: string) =>
      renderTopicContent(optionValue) ?? renderCollectionContent(optionValue),
    [renderTopicContent, renderCollectionContent]
  );

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Stack>
        <Typography variant="h5" fontWeight={500} fontSize={16}>
          Intent Activity
        </Typography>
      </Stack>

      <ExportDataTypeInclusionSelector
        dataType={EXPORT_DATA_TYPES.INTENT}
        options={options}
        inclusion={intentInclusion}
        setInclusion={setIntentInclusion}
        renderOptionContent={renderOptionContent}
      />

      <IntentThresholdSelector
        value={intentSignalScore}
        onChange={setIntentSignalScore}
        disabled={intentInclusion === EXPORT_INCLUSION_TYPES.NONE}
      />

      {universeEnabled && (
        <Stack data-testid="intent-field-config-selector">
          <FieldConfigurationSelector
            fields={intentExportFields}
            firmographicsFields={firmographicsExportFields}
            value={
              intentFieldConfiguration ?? {
                inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
                fields: [],
              }
            }
            onChange={setIntentFieldConfiguration ?? (() => {})}
            title="Intent Fields"
            checkboxLabel="Export All Intent Activity Fields"
            // Matches OppGen: the note + force-injection of topic_id are only
            // active behind the universe_enforce_mandatory_fields_for_tracking flag.
            requiredField={
              enforceMandatoryFields
                ? {
                    values: ["topic_id"],
                    note: "Note: Topic ID will always be included in your export for proper intent topic identification.",
                  }
                : undefined
            }
            disabled={intentInclusion === EXPORT_INCLUSION_TYPES.NONE}
          />
        </Stack>
      )}
    </Stack>
  );
};
